/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { TOPOLOGY_ROOM } from "../../RoomNameConstants";
import { Panel, Topology, BackgroundPanel, TableAdvanced, Spinner } from "vr-react-components";
import JobTooltip from "../../../widgets/JobTooltip";
import JobRoomWorkstationTooltip from "../../../widgets/JobRoomWorkstationTooltip";
import JobLogPanel from "../../../widgets/JobLog/JobLogPanel";
import { getFormattedTime } from "../../../utils/TimeUtils";
import { handleGoToTopologyRoom } from "../../../handlers/TopologyRoomHandlers";
import { handleSetKeyboard } from "../../../handlers/PrivateHandlers";
import { handleGoToDelayAnalysisRoom } from "../../../handlers/DelayAnalysisRoomHandlers";
import { setOpenedModal } from "../../../store/slice/roomSlices/jobRoomSlice";

const LeftPanelVR = React.memo(({ width, height }) => {
    const lastDefinitionChanges = useSelector((state) => state.jobroom.lastDefinitonChangesData);
    const selectedJob = useSelector((state) => state.jobroom.selectedJob);
    const graphData = useSelector((state) => state[TOPOLOGY_ROOM].graph.data)

    const isLoading = graphData?.links?.length === 0 && graphData?.nodes?.length === 0;
    const canModify = useSelector((state) => state.private.device.canModify);
    const isJobLogOpened = useSelector((state) => state.jobroom.openedModal);

    const dispatch = useDispatch();

    const innerWidth = (width / 2) * 0.9;
    const innerHeight = height * 0.9;
    const textSize = 50 / innerWidth;

    const jobDependenciesGraph = {
        nodes: [{ id: 0 }, { id: 1 }, { id: 2 }],
        links: [
            { source: 1, target: 0 },
            { source: 2, target: 0 }
        ],
        nodeGenerator: (node) => {
            const domNode = document.createElement("a-entity");
            domNode.setAttribute("geometry", {
                primitive: "box",
                height: 3,
                width: 6,
                depth: 1,
            });
            domNode.setAttribute("material", {
                color: "#47DECF",
            });

            return domNode;
        },
    };

    const jobDelayAnalisysGraph = {
        nodes: [{ id: 0 }, { id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }],
        links: [
            { source: 0, target: 1 },
            { source: 0, target: 2 },
            { source: 1, target: 3 },
            { source: 1, target: 4 },
        ],
        nodeGenerator: (node) => {
            const domNode = document.createElement("a-entity");
            domNode.setAttribute("geometry", {
                primitive: "box",
                height: 3,
                width: 6,
                depth: 1,
            });
            domNode.setAttribute("material", {
                color: "#FFAF38",
            });

            return domNode;
        },
    };

    return (
        <>
            <BackgroundPanel
                width={width / 2}
                height={height}
                position={`${-width / 4} 0 0`}
                rotation="0 30 0"
            >
                {isJobLogOpened === "jobLog" ?
                    <>
                        <a-entity position={`${width * 0.05 + 0.48} 1.33 0.001`}>
                            <a-circle
                                id="back"
                                class={canModify ? "collidable" : ""}
                                radius={height * 0.04}
                                color="#545454"
                                onClick={() => dispatch(setOpenedModal(null))}
                                opacity={0.9}
                                animation__mouseenter_color={`property: color; to: #121212; easing: linear; startEvents: mouseenter; dur: 150`}
                                animation__mouseleave_color={`property: color; to: #545454; easing: linear; startEvents: mouseleave; dur: 150`}
                            />
                            <a-image
                                height={height * 0.05}
                                width={height * 0.05}
                                rotation="0 0 180"
                                position="0 0 0.001"
                                src="#Back"
                            />
                        </a-entity>
                        <JobLogPanel
                            width={innerWidth}
                            height={innerHeight * 0.5 - height * 0.005 - 0.22}
                            aframeClasses={canModify ? "collidable" : ""}
                            position={`0 ${height / 2 - innerHeight * 0.25 - height * 0.05 - 0.08} ${innerWidth / 100
                                }`}
                            jobLog={selectedJob.jobLog}
                            backgroundColor="#262626"
                            linesPerPage={15}
                            textSizeDecimal={45 / (innerHeight / 2)}
                        />
                        <Panel
                            width={innerWidth}
                            height={innerHeight * 0.5 - height * 0.005}
                            backgroundColor="#262626"
                            aframeClasses={canModify ? "collidable" : ""}
                            position={`0 ${-height / 2 + innerHeight * 0.25 + height * 0.05} ${innerWidth / 100
                                }`}
                            panel
                            title="Last definition version changes"
                        >
                            <TableAdvanced
                                keyboardHandler={(type, visible, callback) => {
                                    handleSetKeyboard(type, visible, callback)
                                }}
                                aframeClasses={canModify ? "collidable" : ""}
                                width={innerWidth * 0.9}
                                height={innerHeight * 0.4}
                                data={[
                                    ["Date", "User", "Reason"],
                                    ...(lastDefinitionChanges
                                        ? lastDefinitionChanges.map((x) => [
                                            getFormattedTime(x[0]),
                                            x[1],
                                            x[2],
                                        ])
                                        : []),
                                ]}
                                position={`0 ${innerHeight * 0.2} ${innerWidth / 100}`}
                            />
                        </Panel>
                    </> : <>
                        <JobTooltip
                            textSizeDecimal={textSize}
                            backgroundColor="#262626"
                            width={innerWidth}
                            height={innerHeight * 0.5 - height * 0.005}
                            position={`0 ${height / 2 - innerHeight * 0.25 - height * 0.05} ${innerWidth / 100
                                }`}
                            element={{
                                ...selectedJob,
                                lastDefinitionChanges:
                                    lastDefinitionChanges && lastDefinitionChanges[0]
                                        ? lastDefinitionChanges[0][0]
                                        : null,
                            }}
                            noJobLog
                        />
                        <JobRoomWorkstationTooltip
                            backgroundColor="#262626"
                            width={innerWidth / 2 - innerWidth / 200}
                            height={innerHeight * 0.5 - height * 0.005}
                            position={`${-innerWidth / 4} ${-height / 2 + innerHeight * 0.25 + height * 0.05
                                } ${innerWidth / 100}`}
                            textSizeDecimal={textSize}
                            element={{
                                name: selectedJob.workstation.name,
                                type: selectedJob.workstation.type,
                                limit: selectedJob.workstation.limit,
                                runningJobs: selectedJob.workstationJobsDetails
                                    ? selectedJob.workstationJobsDetails.running
                                    : 0,
                                status: selectedJob.workstation.status,
                                jobReady: selectedJob.workstationJobsDetails
                                    ? selectedJob.workstationJobsDetails.ready
                                    : 0,
                            }}
                        />
                        <a-entity
                            position={`${innerWidth / 4} ${-height / 2 + innerHeight * 0.25 + height * 0.05
                                } ${innerWidth / 100}`}
                        >
                            {isLoading ?
                                <Spinner radius={0.15} position={'0 0.33 0.01'} /> :
                                <Panel
                                    panel
                                    resize
                                    title="Jobstream view"
                                    onClick={() => {
                                        handleGoToTopologyRoom(selectedJob)
                                    }}
                                    class={canModify ? "collidable" : ""}
                                    aframeClasses={canModify ? "collidable" : ""}
                                    resizePrecision={0.1}
                                    width={innerWidth / 2 - innerWidth / 200}
                                    height={innerHeight * 0.25 - height * 0.005}
                                    position={`0 ${innerHeight * 0.125} 0`}
                                    backgroundColor="#262626"
                                    textSizeDecimal={textSize}
                                >
                                    <Topology
                                        data={jobDependenciesGraph}
                                        dimensions="2"
                                        dagMode="td"
                                        levelDistance="27"
                                        cooldownTime="1000"
                                    />
                                </Panel>
                            }
                            <Panel
                                panel
                                resize
                                title="Delay analysis"
                                onClick={() => {
                                    handleGoToDelayAnalysisRoom(selectedJob)
                                }}
                                class={canModify ? "collidable" : ""}
                                aframeClasses={canModify ? "collidable" : ""}
                                resizePrecision={0.1}
                                width={innerWidth / 2 - innerWidth / 200}
                                height={innerHeight * 0.25 - height * 0.005}
                                position={`0 ${-innerHeight * 0.125}`}
                                backgroundColor="#262626"
                                textSizeDecimal={textSize}
                            >
                                <Topology
                                    data={jobDelayAnalisysGraph}
                                    dimensions="2"
                                    dagMode="lr"
                                    levelDistance="30"
                                    cooldownTime="1000"
                                />
                            </Panel>
                        </a-entity>
                    </>
                }
            </BackgroundPanel>
        </>
    );
});

export default LeftPanelVR